import { useEffect, useState } from "react";
import {
  FloatingPortal,
  flip,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";

interface CellRendererBaseProps {
  params: any;
  children: React.ReactNode;
  nonEditableReason?: string;
}

export const CellRendererBase = ({
  params,
  children,
  nonEditableReason,
}: CellRendererBaseProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom-start",
    middleware: [shift(), flip({ fallbackAxisSideDirection: "end" })],
  });

  const dismiss = useDismiss(context);

  const isCurrentCell = (rowIndex: number, colId: string) => {
    return params.node.rowIndex === rowIndex && params.colDef.colId === colId;
  };

  const handleDoubleClicked = (event: any) => {
    if (isCurrentCell(event.rowIndex, event.column.colId) && !params.colDef.editable?.(params)) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (event: any) => {
    if (event.key !== "Enter") return;
    const api = params?.api;
    if (!api || typeof api.getFocusedCell !== "function" || api.isDestroyed?.()) return;
    const focusedCell = api.getFocusedCell();
    if (
      focusedCell &&
      isCurrentCell(focusedCell.rowIndex, focusedCell.column.colId) &&
      !params.colDef.editable
    ) {
      setIsOpen(true);
    }
  };

  useEffect(() => {
    const api = params?.api;
    api?.addEventListener?.("cellDoubleClicked", handleDoubleClicked);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      if (api && typeof api.isDestroyed === "function" && !api.isDestroyed()) {
        api.removeEventListener?.("cellDoubleClicked", handleDoubleClicked);
      }
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className="flex h-full w-full min-w-0 items-center px-3"
      >
        {children}
      </div>
      {isOpen && nonEditableReason && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles }}
            {...getFloatingProps()}
            className="ml-[-1px] mt-px max-w-72 rounded border border-border bg-popover px-3 py-2.5 text-xs text-foreground shadow-lg"
          >
            {nonEditableReason}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
